<template>
    <div class="find-office-map-container">
        <div class="find-office-map-sidebar">
            <div class="controls-container">
                <div
                    :class="['controls-heading', sidebarScrollTop > 50 ? 'controls-heading-hidden' : '']"
                >
                    <h1>
                        Our Offices
                    </h1>
                </div>
                <input
                    id="autocomplete"
                    class="input autocomplete-input controls-address"
                    type="text"
                    placeholder="Your street address..."
                >

                <button
                    class="controls-location-button"
                    @click="clickUseLocation"
                >
                    <span>Use my location</span>
                    <img
                        :src="targetMarkerUrl"
                    >
                </button>
                <div class="controls-radiogroup">
                    <input
                        id="radio_sales"
                        v-model="businessType"
                        type="radio"
                        name="officetype"
                        value="Sales"
                        checked
                    >
                    <label for="radio_sales">Sales</label>

                    <input
                        id="radio_rentals"
                        v-model="businessType"
                        type="radio"
                        name="officetype"
                        value="Rentals"
                    >
                    <label for="radio_rentals">Rentals</label>

                    <input
                        id="radio_commercial"
                        v-model="businessType"
                        type="radio"
                        name="officetype"
                        value="Commercial"
                    >
                    <label for="radio_commercial">Commercial</label>
                </div>
            </div>

            <div
                class="office-list-container"
                @scroll="sidebarScroll"
            >
                <p class="office-list-meta">
                    Ordered {{ selectedPosition ? 'by nearest' : 'alphabetically' }}
                </p>

                <ul class="office-list">
                    <li
                        v-for="office in filteredOffices"
                        :key="office.id"
                        class="office-list-item"
                        @mouseover="officeListItemMouseOver(office)"
                        @mouseleave="officeListItemMouseLeave(office)"
                    >
                        <div class="office-title">
                            <a
                                :href="office.url"
                                target="_blank"
                                class="h4 u-pull--left"
                            >
                                {{ office.name }}
                            </a>
                            <small v-if="office.distance > 0">
                                {{ office.distance.toFixed() }}km
                            </small>

                            <span class="badge u-pull--right">
                                {{ office.business_type_short }}
                            </span>
                        </div>

                        <p class="office-address">
                            {{ office.address }}
                        </p>

                        <a
                            class="office-phone"
                            :href="`tel:${office.phone}`"
                        >
                            {{ office.phone }}
                        </a>

                        <a
                            class="office-directions"
                            :href="directionsUrl(office.lat, office.lng)"
                            target="_blank"
                        >
                            Directions
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <div
            id="map"
            style="width: 100%; height: 85vh;"
        />
    </div>
</template>

<script>
import { latLngDistance, } from '../helpers';
import { mapStyles, } from './mapStyles';

export default {
    props: {
        offices: {
            type: Array,
            required: true,
        },
        officeMarkerUrl: {
            type: String,
            required: true,
        },
        targetMarkerUrl: {
            type: String,
            required: true,
        },
    },
    data: function () {
        return {
            businessType: 'Sales',
            autocomplete: null,
            hoveredOfficeID: null,
            map: null,
            markers: [],
            selectedPosition: null,
            sidebarScrollTop: 0,
            targetMarker: null,
            zoom: 6,
            icon: {
                scaledSize: new google.maps.Size(25, 32),
                size: new google.maps.Size(25, 32),
                url: this.officeMarkerUrl,
            },
            infoWindow: null,
        };
    },
    computed: {
        filteredOffices: function () {
            let filtered = this.offices.filter(o => {
                if (o.business_type_short == 'SalesRentals') {
                    return ['Sales', 'Rentals',].includes(this.businessType);
                }

                return o.business_type_short == this.businessType;
            });

            if (this.selectedPosition) {
                filtered = filtered.sort((a, b) => {
                    return a.distance - b.distance;
                });
            }

            return filtered;
        },
    },
    watch: {
        selectedPosition: function () {
            for (let i in this.offices) {
                let o = this.offices[i];
                o.distance = latLngDistance(this.selectedPosition.lat, this.selectedPosition.lng, o.lat, o.lng);
                this.$set(this.offices, i, o);
            }

            // @TODO: Hacky?
            this.$forceUpdate();
        },
        filteredOffices: function () {
            this.drawOfficeMarkers();
        },
        hoveredOfficeID: function (newVal, oldVal) {
            for (let i in this.offices) {
                if (this.offices[i].id == newVal) {
                    this.offices[i].marker.setAnimation(google.maps.Animation.BOUNCE);
                } else if (this.offices[i].id == oldVal) {
                    this.offices[i].marker.setAnimation(null);
                }
            }
        },
    },
    mounted () {
        this.initMap();
        this.initMarkers();
    },
    methods: {
        initMap () {
            let mobile = document.body.clientWidth < 740;
            this.map = new google.maps.Map(document.getElementById('map'), {
                center: {
                    lat: mobile ? -25.3 : -29.08,
                    lng: mobile ? 23.17 : 20.15,
                },
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_BOTTOM,
                },
                scrollwheel: false,
                zoom: mobile ? this.zoom -1 : this.zoom,
                styles: mapStyles,
            });

            this.autocomplete = new google.maps.places.Autocomplete(document.getElementById('autocomplete'));
            this.autocomplete.bindTo('bounds', this.map);
            this.autocomplete.setFields([ 'geometry', ]);
            this.autocomplete.addListener('place_changed', this.placeChanged);

            this.targetMarker = new google.maps.Marker({
                map: this.map,
                anchorPoint: new google.maps.Point(0, -12),
            });

            let infoWindowClass = this.createInfoWindowClass();
            this.infoWindow = new infoWindowClass();
            this.infoWindow.setMap(this.map);
            this.map.addListener('click', (function () {
                this.infoWindow.close();
            }).bind(this));
        },
        initMarkers() {
            let Popup = this.createInfoWindowClass();
            for (let i in this.offices) {
                let o = this.offices[i];
                this.offices[i].marker = this.markerFactory(o, Popup);
            }

            this.drawOfficeMarkers();

        },
        drawOfficeMarkers () {
            for (let i in this.offices) {
                // Show this office's marker?
                this.offices[i].marker.setMap(this.offices[i].business_type_short == this.businessType ? this.map : null);
            }
        },
        placeChanged () {
            this.targetMarker.setVisible(false);

            const place = this.autocomplete.getPlace();
            if (!place.geometry) {
                // User entered the name of a Place that was not suggested and
                // pressed the Enter key, or the Place Details request failed.
                return;
            }

            this.setSelectedPosition(place.geometry.location.lat(), place.geometry.location.lng());
        },
        setSelectedPosition (lat, lng) {
            // Offset the lng here a touch so it's more centered in the part
            // of the map that's not covered by the sidebar.
            this.map.setCenter(new google.maps.LatLng(lat, lng - 0.01));
            this.map.setZoom(14);

            this.targetMarker.setPosition(new google.maps.LatLng(lat, lng));
            this.targetMarker.setVisible(true);

            // @TODO: pop up the nearest office's details?

            this.selectedPosition = {
                lat: lat,
                lng: lng,
            };
        },
        sidebarScroll (e) {
            this.sidebarScrollTop = e.target.scrollTop;
        },
        createInfoWindowClass () {
            // this class modified from the Google Maps API Docs page on custom popups
            function Popup() {
                this.position = new google.maps.LatLng(0, 0);

                this.infowindowAnchor = document.createElement('div');
                this.infowindowAnchor.classList.add('infowindow-anchor');
                this.infowindowAnchor.innerHTML = '';
                this.infowindowAnchor.addEventListener('mouseleave', this.close.bind(this));

                this.containerDiv = document.createElement('div');
                this.containerDiv.classList.add('infowindow-container');
                this.containerDiv.appendChild(this.infowindowAnchor);

                this.visible = false;
                google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.containerDiv);
            }
            Popup.prototype = Object.create(google.maps.OverlayView.prototype);

            Popup.prototype.onAdd = function () {
                this.getPanes().floatPane.appendChild(this.containerDiv);
            };

            Popup.prototype.onRemove = function () {
                if (this.containerDiv.parentElement) {
                    this.containerDiv.parentElement.removeChild(this.containerDiv);
                }
            };

            Popup.prototype.open = function (content, position) {
                this.visible = true;
                this.position = position;
                this.infowindowAnchor.innerHTML = content;
                this.draw.bind(this)();
            };

            Popup.prototype.close = function () {
                this.visible = false;
                this.draw.bind(this)();
            };

            Popup.prototype.draw = function () {
                let divPosition = this.getProjection().fromLatLngToDivPixel(this.position);

                if (this.visible) {
                    this.containerDiv.style.left = divPosition.x + 'px';
                    this.containerDiv.style.top = divPosition.y + 'px';
                }

                let display = this.visible ? 'block' : 'none';
                if (this.containerDiv.style.display !== display) {
                    this.containerDiv.style.display = display;
                }
            };
            return Popup;
        },
        markerFactory (office) {
            let marker = new google.maps.Marker({
                icon: this.icon,
                position: new google.maps.LatLng(office.lat, office.lng),
                scaledSize: new google.maps.Size(47, 57),
                title: office.name,
                map: this.map,
            });

            let contentString = `
                <div class="infowindow">
                    <p>
                        <a
                            href="${office.url}"
                            target="_blank"
                            class="h4 office-title"
                        >
                            ${office.name}
                        </a>
                    </p>
                    <p>
                        <span class="office-category">
                            ${office.business_type_short}
                        </span>
                    </p>
                    <p class="office-address">
                        ${office.address}
                    </p>
                    <div>
                        <a
                            class="office-phone"
                            href="tel:${office.phone}"
                        >
                            ${office.phone}
                        </a>
                        <a
                            class="office-directions"
                            href="${this.directionsUrl(office.lat, office.lng)}"
                            target="_blank"
                        >
                            Directions
                        </a>
                    </div>
                </div>
                `;

            let openInfoWindow = function () {
                this.infoWindow.open(contentString, marker.position);
            };

            marker.addListener('mouseover', openInfoWindow.bind(this));
            marker.addListener('click', openInfoWindow.bind(this));

            return marker;
        },
        directionsUrl (lat, lng) {
            let url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
            if (this.selectedPosition) {
                url += `&origin=${this.selectedPosition.lat},${this.selectedPosition.lng}`;
            }

            return url;
        },
        clickUseLocation () {
            navigator.geolocation.getCurrentPosition((l) => {
                this.setSelectedPosition(l.coords.latitude, l.coords.longitude);
            }, function (error) {
                dd('getCurrentPosition error', error);
            });
        },
        officeListItemMouseOver (office) {
            this.hoveredOfficeID = office.id;
        },
        officeListItemMouseLeave () {
            this.hoveredOfficeID = null;
        },
    },
};
</script>
